import { expandProperties } from '@ember/object/computed';
import { cached } from '@glimmer/tracking';
import { isDecoratorArgs, isPropertyDecoratorArgs } from 'decorator-setup';
import { load } from 'ember-async-data';

import { dateMin, dateMax } from 'rise-ui/utils/date.js';
import { isSet } from 'rise-ui/utils/nullish.js';
import { get } from 'rise-ui/utils/object.js';
import { isAsync, isPromise } from 'rise-ui/utils/promise.js';

/**
 * Note: prefer these decorators to `@ember/object/computed` macros, which
 * don't support consuming getter dependencies without `@dependentKeyCompat`.
 */

export const value = value => new ComputedDecoratorValue(value);

/**
 * Create property getter which gives one of given or defaultValue functions
 * @decorator
 */
export function fn(...paths) {
  const options = typeof paths[paths.length - 1] === 'object' ? paths.pop() : {};
  let { defaultValue } = options;
  if (typeof defaultValue !== 'function') {
    const dv = defaultValue;
    defaultValue = () => dv;
  }
  if (!paths.length || isDecoratorArgs(...arguments)) {
    throw new Error('@fn expects path argument(s)');
  }
  return function(target, propertyKey, descriptor) {
    if (!isPropertyDecoratorArgs(...arguments)) {
      throw new Error('@fn should be applied to a property');
    }
    return {
      get() {
        let fn;
        for (const path of paths) {
          expandProperties(path, p => {
            if (fn) {
              return;
            }
            const f = get(this, p);
            if (typeof f === 'function') {
              fn = f;
            }
          });
          if (fn) {
            return fn;
          }
        }
        return defaultValue;
      },
      set() {
        throw new Error('@fn property cannot be set');
      },
    };
  };
}

/**
 * Creates property getter which gives the boolean NOT of first given argument
 * @decorator
 */
export function not(path) {
  if (isDecoratorArgs(...arguments)) {
    throw new Error('@not expects path argument');
  }
  return function(target, propertyKey, descriptor) {
    if (!isPropertyDecoratorArgs(...arguments)) {
      throw new Error('@not should be applied to a property');
    }
    return {
      get() {
        return !get(this, path);
      },
      set() {
        throw new Error('@not property cannot be set');
      },
    };
  };
}

/**
 * @decorator
 */
// TODO a way to exclude or account for ember-data models without `value` property
export function latest(path) {
  if (!path || isDecoratorArgs(...arguments)) {
    throw new Error('@latest expects path argument or when/value options');
  }
  return function(target, propertyKey, descriptor) {
    if (!isPropertyDecoratorArgs(...arguments)) {
      throw new Error('@latest should be applied to a property');
    }
    let previous, proxy;
    let initial = true;
    descriptor = {
      get: function() {
        // keepLatest functionality
        if (path && typeof path === 'object') {
          const { when, value, isEmpty } = path;
          const val = value();
          if (when()) {
            if (previous === undefined && initial) {
              initial = false;
              return val;
            }
            return (previous = isEmpty?.(val) ? previous : val);
          }
          return (previous = val);
        }

        let val = get(this, path);
        if (!isAsync(val)) {
          if (!isPromise(val)) {
            // Without when clause or other reactive trigger, just return it
            return val;
          }
          // Create reactive async from promise
          val = load(val);
        }
        if (val.isPending) {
          // For some reason we get here twice, so we cache proxy
          // here to avoid recreating it again unnecessarily.
          val = proxy ??= new Proxy(val, {
            get(target, prop, receiver) {
              if (prop === 'value' && previous !== undefined) {
                // Give previous value while current value is pending
                return previous.value;
              }
              return Reflect.get(...arguments);
            }
          });
        } else {
          // Load value into new TrackedAsync or they remained connected
          previous = load(val.value);
          proxy = null;
        }
        return val;
      },
      set() {
        throw new Error('@latest property cannot be set');
      },
    };
    return cached(target, propertyKey, descriptor) || descriptor;
  };
}

/**
 * Create property boolean getter whether each given value is loosely equal to the previous value
 * @decorator
 */
export const eq = compareDecorator('@eq', (a, b) => a == b);

/**
 * Create property boolean getter whether all given are strictly equal
 * @decorator
 */
export const strictEq = compareDecorator('@strictEq', (a, b) => a === b);

/**
 * Create property boolean getter whether each given value is lesser than the previous value
 * @decorator
 */
export const lt = compareDecorator('@lt', (a, b) => a < b);

/**
 * Create property boolean getter whether each given value is lesser than or equal to the previous value
 * @decorator
 */
export const lte = compareDecorator('@lte', (a, b) => a <= b);

/**
 * Create property boolean getter whether each given value is greater than the previous value
 * @decorator
 */
export const gt = compareDecorator('@gt', (a, b) => a > b);

/**
 * Create property boolean getter whether each given value is greater than or equal to the previous value
 * @decorator
 */
export const gte = compareDecorator('@gte', (a, b) => a >= b);

/**
 * Create property getter which gives first is-set of given values
 * @decorator
 */
export const one = computedDecorator(
  '@one',
  values => values.find(v => isSet(v)),
  [ '...paths' ],
);

/**
 * Create property getter which gives first truthy of given
 * @decorator
 */
export const or = computedDecorator(
  '@or',
  values => values.find(v => v),
  [ '...paths' ],
);

/**
 * Create property getter which one way aliases given value
 * Note: @readOnly does not expand braces; to derive from more than one property,
 * including using braces, use something like @one or @or.
 * @decorator
 */
export const readOnly = computedDecorator(
  '@readOnly',
  ([ value ]) => value,
  [ 'path' ],
  1,
  false,
);


export function expandDeps(...deps) {
  const expanded = [];
  deps.forEach(path => expandProperties(path, props => expanded.push(props)));
  return expanded;
}

/**
 * Compare dependent values with given function
 * @param {string[]} values
 * @param {function} fn
 */
export function compareFn(...values) {
  const fn = values.pop();
  if (!values.length) {
    return;
  }
  return values.reduce((p, c) => fn(c, p));
}

/**
 * Create property getter which gives least of given dates
 * @decorator
 */
export const leastDate = computedDecorator(
  '@leastDate',
  values => compareFn(...values, dateMin),
  [ '...paths' ],
  1,
);

/**
 * Create property getter which gives greatest of given dates
 * @decorator
 */
export const greatestDate = computedDecorator(
  '@greatestDate',
  values => compareFn(...values, dateMax),
  [ '...paths' ],
  1,
);

export class ComputedDecoratorValue {
  constructor(value) {
    this.value = value;
  }
}

/**
 * Generate computed property decorator
 * @param {string} name
 * @param {function} getFn
 * @param {string[]} [requireArgs]
 * @param {number|null} [maxArgs]
 * @param {boolean} [expandBraces]
 * @decorator
 */
export function computedDecorator(name, getFn, requireArgs = null, maxArgs = null, expandBraces = true) {
  function computedDecorator(...args) {
    const isNotFactory = isDecoratorArgs(...args);
    if (isNotFactory) {
      args = [];
    }
    if (requireArgs?.length && (isNotFactory || args.length < requireArgs.length)) {
      throw new Error(`${name} expects arguments: ${requireArgs.join(', ')}`);
    }
    maxArgs ??= Infinity;
    if (!isNotFactory && args.length > maxArgs) {
      throw new Error(`${name} expects max arguments: ${maxArgs}`);
    }
    return isNotFactory ? propertyDecorator(...args) : propertyDecorator;
    function propertyDecorator(target, propertyKey, descriptor) {
      if (!isPropertyDecoratorArgs(...arguments)) {
        throw new Error(`${name} should be applied to a property`);
      }
      return {
        get() {
          const argValues = [];
          const pushProp = prop => argValues.push(get(this, prop));
          for (let i = 0; i < args.length; ++i) {
            const arg = args[i];
            if (!arg) {
              argValues.push(undefined);
              continue;
            }
            if (arg instanceof ComputedDecoratorValue) {
              argValues.push(arg.value);
              continue;
            }
            if (expandBraces) {
              expandProperties(arg, pushProp);
            } else {
              pushProp(arg);
            }
          }
          return getFn.call(this, argValues, this);
        },
        set() {
          throw new Error(`${name} property cannot be set`);
        },
      };
    }
  }
  computedDecorator.value = value;
  return computedDecorator;
}

/**
 * Generate compare property decorator
 * @param {string} name
 * @param {function} compareFn
 * @param {number} [maxArgs]
 * @param {boolean} [expandBraces]
 * @decorator
 */
function compareDecorator(name, compareFn, maxArgs = Infinity, expandBraces = true) {
  return computedDecorator(
    name,
    (args, o) => {
      for (let i = 1, a = args[0], b = args[i]; i < args.length; ++i, a = b) {
        if (!compareFn.call(o, a, b)) {
          return false;
        }
      }
      return true;
    },
    [ 'value', maxArgs === 2 ? 'comparison' : '...comparisons' ],
    maxArgs,
    expandBraces,
  );
}
